import { Button, Text } from '@/components'
import { useUnenrollmentRouteContext } from '..'
import { useAppDispatch, useAppSelector } from '@/hooks'
import {
  completeUnenrollment,
  selectInProgressUnenrollment,
} from '@/reducers/inProgressUtilityProgramUnenrollments'
import unenrollmentSchema, {
  UnenrollmentValidationSchema,
} from '@/utils/validation/unenrollmentSchema'
import { ok } from 'neverthrow'
import { safeValidateSync } from '@/utils/validation/safeValidate'
import { useMemo, useState } from 'react'
import { ProgramDevices, selectProgramDevices } from '@/reducers/programsNew'
import { AlertMessage } from '@/app/components'
import { Dot as DotIcon, LoaderCircle } from 'lucide-react'
import Separator from '@/app/components/Separator/Separator'
import useEnrollmentActions from '@/hooks/useEnrollmentActions'
import { useNavigation } from '@/app/hooks'
import { useSurveyResponder } from '@/app/components/SurveyObserver'
import { AllUnenrollmentReasons } from '../unenroll-reason/constants'
import useUtilityProgramRewards from '@/app/hooks/useUtilityProgramRewards'

function useViewModel() {
  const { utilityProgram } = useUnenrollmentRouteContext()
  const { unenrollDevices } = useEnrollmentActions()
  const { submitResponse } = useSurveyResponder('ProgramUnenrollment')
  const { incentivesExist } = useUtilityProgramRewards(utilityProgram.id)
  const navigate = useNavigation()
  const dispatch = useAppDispatch()
  const inProgressUnenrollment = useAppSelector(
    selectInProgressUnenrollment(utilityProgram.id),
  )
  const unenrollmentWarningText =
    utilityProgram.view_config_json.unenrollmentWarningText
  const validationResult = useMemo(() => {
    if (!inProgressUnenrollment) {
      return ok(null)
    }
    return safeValidateSync(unenrollmentSchema, inProgressUnenrollment)
  }, [inProgressUnenrollment])

  const enrolledDevices = useAppSelector(
    selectProgramDevices(utilityProgram.id),
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const onUnenrollClick = async () => {
    if (!inProgressUnenrollment) {
      return
    }
    setIsSubmitting(true)
    const result = await unenrollDevices({
      utilityProgramId: utilityProgram.id,
      vehicleIds: inProgressUnenrollment.deviceSelection.vehicleIds,
      thermostatIds: inProgressUnenrollment.deviceSelection.thermostatIds,
      chargerIds: inProgressUnenrollment.deviceSelection.chargerIds,
    })
    dispatch(completeUnenrollment(utilityProgram.id))
    await submitResponse({
      reasons: inProgressUnenrollment.unenrollmentReasons,
      message: inProgressUnenrollment.feedback,
    })
    setIsSubmitting(false)
    if (result.isErr()) {
      navigate.push(
        `/utility-program/${utilityProgram.id}/unenroll/state/error`,
      )
    }
    navigate.push(
      `/utility-program/${utilityProgram.id}/unenroll/state/success`,
    )
  }
  return {
    validationResult,
    enrolledDevices,
    onUnenrollClick,
    isSubmitting,
    incentivesExist,
    unenrollmentWarningText,
  }
}
export default function UnenrollReview() {
  const {
    validationResult,
    enrolledDevices,
    onUnenrollClick,
    isSubmitting,
    incentivesExist,
    unenrollmentWarningText,
  } = useViewModel()
  return validationResult.match(
    (content) => (
      <ReviewContent
        content={content}
        devices={enrolledDevices}
        onUnenrollClick={onUnenrollClick}
        isSubmitting={isSubmitting}
        incentivesExist={incentivesExist}
        unenrollmentWarningText={unenrollmentWarningText}
      />
    ),
    () => (
      <div className="flex flex-col gap-4 h-full">
        <AlertMessage variant="error">
          Error occured while validating your responses. Please go back or try
          again later
        </AlertMessage>
      </div>
    ),
  )
}

function Dot() {
  return <DotIcon className="text-grey-200" size={20} />
}

type ReviewContentProps = {
  content: UnenrollmentValidationSchema | null
  devices: ProgramDevices
  onUnenrollClick: () => void
  isSubmitting: boolean
  incentivesExist: boolean
  unenrollmentWarningText: string | undefined
}
function ReviewContent({
  content,
  devices,
  onUnenrollClick,
  isSubmitting,
  incentivesExist,
  unenrollmentWarningText,
}: ReviewContentProps) {
  if (!content) {
    return <></>
  }
  const vehicles = content.deviceSelection.vehicleIds
    .map((id) => devices.vehicles.find((v) => v.id === id))
    .filter((v) => v !== undefined)
  const thermostats = content.deviceSelection.thermostatIds
    .map((id) => devices.thermostats.find((t) => t.id === id))
    .filter((t) => t !== undefined)
  const chargers = content.deviceSelection.chargerIds
    .map((id) => devices.chargers.find((c) => c.id === id))
    .filter((c) => c !== undefined)
  return (
    <div className="flex flex-col h-full">
      <Text variant="h3" className="mb-8">
        Review your unenrollment details
      </Text>
      <Separator className="mb-4" />
      <Text variant="body1" className="mb-2">
        Devices to unenroll
      </Text>
      <ul className="[&>*]:flex [&>*]:flex-row [&>*]:items-center">
        {vehicles.map((v) => (
          <Text key={v.id} variant="body2">
            <Dot />
            {v.car.car_model?.year} {v.car.car_model?.friendly_name}
          </Text>
        ))}
        {thermostats.map((t) => (
          <Text key={t.id} variant="body2">
            <Dot />
            {t.name}
          </Text>
        ))}
        {chargers.map((c) => (
          <Text key={c.id} variant="body2">
            <Dot />
            {c.name ?? 'Charger'}
          </Text>
        ))}
      </ul>
      <Separator className="my-4" />
      <Text variant="body1" className="mb-2">
        Reason{content.unenrollmentReasons.length > 1 ? 's' : ''} for
        unenrollment
      </Text>
      <ul className="[&>*]:flex [&>*]:flex-row [&>*]:items-center">
        {content.unenrollmentReasons.map((r) => (
          <Text key={r.reason} variant="body2">
            <Dot />
            {AllUnenrollmentReasons.find((s) => s.value === r.reason)?.text}
          </Text>
        ))}
      </ul>
      <Separator className="my-4" />
      <Text variant="body1" className="mb-2">
        What went wrong?
      </Text>
      <Text variant="body2" className="italic">
        "{content.feedback}"
      </Text>
      {incentivesExist && (
        <AlertMessage className="mt-8" variant="warning">
          {unenrollmentWarningText ??
            'You’ll no longer earn rewards once you leave the program.'}
        </AlertMessage>
      )}
      <span className="flex-auto" />
      <Button
        id="unenroll-review-button"
        variant="primary"
        disabled={isSubmitting}
        onClick={onUnenrollClick}
        className="flex flex-row items-center mt-8"
      >
        <span>Unenroll{isSubmitting ? 'ing' : ''}</span>
        {isSubmitting && (
          <LoaderCircle size={16} className="animate-spin block" />
        )}
      </Button>
    </div>
  )
}
